import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../t';
import CustomImage from './CustomImage';
import { getRemainingShareDays, getSpaceUsedInUnit } from '../util';
import dayjs from 'dayjs';
import C from '../colors';
import { getPictureThumbnailUrl } from '../pictureUrls';
import { formatMoney } from '../money';
import {
  getSelectionStatus,
  orderWorkflowStep,
  paymentStatusMeta,
  selectionStatusMeta,
} from '../orderStatus';

function Icon({ name, size = 16 }) {
  const s = { width: size, height: size, fill: 'none', stroke: 'currentColor', strokeWidth: 1.5, strokeLinecap: 'round', strokeLinejoin: 'round' };
  switch (name) {
    case 'photos':  return <svg viewBox="0 0 24 24" {...s}><rect x="3" y="6" width="18" height="13" rx="2"/><path d="m3 16 5-4 4 3 4-4 5 5"/><circle cx="8" cy="10" r="1.2"/></svg>;
    case 'edit':    return <svg viewBox="0 0 24 24" {...s}><path d="M4 20h4l11-11-4-4L4 16zM14 5l4 4"/></svg>;
    case 'share':   return <svg viewBox="0 0 24 24" {...s}><circle cx="6" cy="12" r="2.5"/><circle cx="18" cy="6" r="2.5"/><circle cx="18" cy="18" r="2.5"/><path d="m8 11 8-4M8 13l8 4"/></svg>;
    case 'more':    return <svg viewBox="0 0 24 24" {...s}><circle cx="5" cy="12" r="1.2" fill="currentColor"/><circle cx="12" cy="12" r="1.2" fill="currentColor"/><circle cx="19" cy="12" r="1.2" fill="currentColor"/></svg>;
    case 'check':   return <svg viewBox="0 0 24 24" {...s} strokeWidth="2"><path d="m5 13 4 4L19 7"/></svg>;
    case 'arrow':   return <svg viewBox="0 0 24 24" {...s}><path d="M5 12h14M13 6l6 6-6 6"/></svg>;
    case 'delete':  return <svg viewBox="0 0 24 24" {...s}><path d="M4 7h16M9 7V4h6v3M6 7l1.2 13a1.5 1.5 0 0 0 1.5 1.4h6.6a1.5 1.5 0 0 0 1.5-1.4L18 7"/></svg>;
    case 'stats':   return <svg viewBox="0 0 24 24" {...s}><path d="M18 20V10M12 20V4M6 20v-6"/></svg>;
    default: return null;
  }
}

function StatusPill({ tone = 'neutral', children, testId, status }) {
  const palettes = {
    success: { bg: C.successBg, color: C.successFg, dot: C.successFg },
    warm:    { bg: C.warningBg, color: C.warningFg, dot: C.warningDot },
    brand:   { bg: C.brandSoft, color: '#B7406E', dot: C.brand },
    danger:  { bg: C.dangerBg, color: C.dangerFg, dot: C.dangerDot },
    neutral: { bg: C.neutralBg, color: C.neutralFg, dot: C.neutralDot },
  };
  const p = palettes[tone] || palettes.neutral;
  return (
    <span data-testid={testId} data-status={status} style={{
      display: 'inline-flex', alignItems: 'center', gap: 6,
      padding: '3px 8px', borderRadius: 10,
      background: p.bg, color: p.color,
      fontSize: 11, fontWeight: 600, letterSpacing: 0.2,
      whiteSpace: 'nowrap',
    }}>
      <span style={{ width: 5, height: 5, borderRadius: 3, background: p.dot }}/>
      {children}
    </span>
  );
}

function StatCell({ label, value, hint, accent, warn, testId }) {
  return (
    <div data-testid={testId} style={{ minWidth: 0 }}>
      <div style={{
        fontFamily: C.mono, fontSize: 10, letterSpacing: 1.2,
        textTransform: 'uppercase', color: C.ink3, marginBottom: 4,
      }}>{label}</div>
      <div style={{
        fontFamily: C.serif, fontSize: 22, lineHeight: 1, color: warn ? C.dangerFg : accent ? C.brand2 : C.ink,
        fontVariantNumeric: 'tabular-nums',
      }}>{value}</div>
      {hint && <div style={{ fontSize: 11, color: C.ink3, marginTop: 4 }}>{hint}</div>}
    </div>
  );
}

function PhotoStrip({ order, count = 7 }) {
  const [expanded, setExpanded] = useState(false);
  const pics = order.pictures || [];
  const total = order.Album?.pictureCount || pics.length;
  const visible = expanded ? Math.min(16, total) : Math.min(count, total);
  const remaining = total - count;

  if (total === 0) {
    return (
      <div style={{
        padding: '16px 0', textAlign: 'center',
        fontSize: 13, color: C.ink3, fontFamily: C.sans,
      }}>
        暂无照片
      </div>
    );
  }

  return (
    <div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: `repeat(${count + 1}, 1fr)`,
        gap: 6,
      }}>
        {Array.from({ length: Math.min(count, total) }, (_, i) => {
          const pic = pics[i];
          return (
            <PhotoThumb
              key={i}
              pic={pic}
              index={i}
              cover={i === 0 && !pic?.isCover ? true : pic?.isCover}
              selected={i < (order.selectedPhotoCount || 0)}
              watermarked={order.openWatermark}
            />
          );
        })}
        {/* filler empty slots */}
        {total < count && Array.from({ length: count - total }, (_, i) => (
          <div key={`empty-${i}`} style={{
            aspectRatio: '4 / 3',
            borderRadius: 6,
            background: C.paper2,
            border: `1px dashed ${C.hair2}`,
          }}/>
        ))}
        {/* +N tile */}
        {remaining > 0 && (
          <button onClick={() => setExpanded(!expanded)} style={{
            aspectRatio: '4 / 3',
            border: `1px dashed ${C.hair2}`,
            background: C.paper2,
            color: C.ink2,
            borderRadius: 6, cursor: 'pointer',
            display: 'grid', placeItems: 'center',
            fontFamily: 'inherit', padding: 0, lineHeight: 1.1,
          }}>
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontFamily: C.serif, fontSize: 18, color: C.ink }}>
                +{remaining}
              </div>
              <div style={{ fontFamily: C.mono, fontSize: 9, color: C.ink3, letterSpacing: 0.4, textTransform: 'uppercase', marginTop: 2 }}>
                {expanded ? '收起' : '展开'}
              </div>
            </div>
          </button>
        )}
      </div>
      {expanded && remaining > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: `repeat(${count + 1}, 1fr)`, gap: 6, marginTop: 6,
        }}>
          {Array.from({ length: Math.min(remaining, 16 - count) }, (_, i) => {
            const pic = pics[count + i];
            return (
              <PhotoThumb
                key={i + count}
                pic={pic}
                index={count + i}
                selected={count + i < (order.selectedPhotoCount || 0)}
                watermarked={order.openWatermark}
              />
            );
          })}
        </div>
      )}
    </div>
  );
}

function PhotoThumb({ pic, index, cover, selected, watermarked }) {
  const thumbnailUrl = getPictureThumbnailUrl(pic);
  if (thumbnailUrl) {
    return (
      <div style={{
        position: 'relative',
        aspectRatio: '4 / 3',
        borderRadius: 6,
        overflow: 'hidden',
        border: `1px solid ${C.hair}`,
        background: '#fff',
      }}>
        <CustomImage
          src={thumbnailUrl}
          alt={`pic-${index}`}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
        {cover && (
          <div style={{
            position: 'absolute', top: 4, left: 4,
            padding: '2px 6px',
            background: 'rgba(0,0,0,.6)',
            color: '#fff',
            borderRadius: 3,
            fontFamily: C.mono, fontSize: 9, letterSpacing: 0.5,
          }}>封面</div>
        )}
        {selected && (
          <div style={{
            position: 'absolute', bottom: 4, right: 4,
            width: 16, height: 16, borderRadius: 8,
            background: C.brand,
            display: 'grid', placeItems: 'center',
            color: '#fff',
            boxShadow: '0 2px 4px rgba(0,0,0,.2)',
          }}>
            <Icon name="check" size={10}/>
          </div>
        )}
        {watermarked && (
          <div style={{
            position: 'absolute', inset: 0,
            display: 'grid', placeItems: 'center',
            fontFamily: C.serif, fontSize: 10, color: 'rgba(255,255,255,.4)',
            letterSpacing: 1,
            transform: 'rotate(-20deg)',
            textShadow: '0 1px 2px rgba(0,0,0,.15)',
            pointerEvents: 'none',
          }}>
            图派
          </div>
        )}
      </div>
    );
  }

  // Placeholder for missing pic
  return (
    <div style={{
      position: 'relative',
      aspectRatio: '4 / 3',
      borderRadius: 6,
      background: 'linear-gradient(160deg, #e8d4be 0%, #b58c6e 55%, #6a4530 100%)',
      overflow: 'hidden',
      boxShadow: 'inset 0 0 0 1px rgba(0,0,0,.04)',
    }}>
      <div style={{
        position: 'absolute', inset: 0,
        background: 'radial-gradient(120% 80% at 30% 20%, rgba(255,255,255,.18), transparent 60%)',
        mixBlendMode: 'screen',
      }}/>
      {watermarked && (
        <div style={{
          position: 'absolute', inset: 0,
          display: 'grid', placeItems: 'center',
          fontFamily: C.serif, fontSize: 10, color: 'rgba(255,255,255,.4)',
          letterSpacing: 1,
          transform: 'rotate(-20deg)',
          textShadow: '0 1px 2px rgba(0,0,0,.15)',
          pointerEvents: 'none',
        }}>
          图派
        </div>
      )}
      {cover && (
        <div style={{
          position: 'absolute', top: 4, left: 4,
          padding: '2px 6px',
          background: 'rgba(0,0,0,.6)',
          color: '#fff',
          borderRadius: 3,
          fontFamily: C.mono, fontSize: 9, letterSpacing: 0.5,
        }}>封面</div>
      )}
      {selected && (
        <div style={{
          position: 'absolute', bottom: 4, right: 4,
          width: 16, height: 16, borderRadius: 8,
          background: C.brand,
          display: 'grid', placeItems: 'center',
          color: '#fff',
          boxShadow: '0 2px 4px rgba(0,0,0,.2)',
        }}>
          <Icon name="check" size={10}/>
        </div>
      )}
    </div>
  );
}

const PIPELINE_STEPS = [
  { key: 'created',  label: '建档' },
  { key: 'uploaded', label: '上传' },
  { key: 'shared',   label: '分享' },
  { key: 'done',     label: '完成' },
];

function StatusPipeline({ order }) {
  const photoTotal = order.Album?.pictureCount || 0;
  const isShared   = !!order.Album?.password;
  const status     = getSelectionStatus(order);
  const isExpired  = status === 'expired';

  const done = {
    created:  true,
    uploaded: photoTotal > 0,
    shared:   isShared,
    done:     status === 'submitted',
  };

  // Current active step index (last done step)
  const activeIdx = PIPELINE_STEPS.reduce((acc, s, i) => done[s.key] ? i : acc, 0);

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 0, marginBottom: 14 }}>
      {PIPELINE_STEPS.map((step, i) => {
        const isDone    = done[step.key];
        const isActive  = i === activeIdx;
        const isLast    = i === PIPELINE_STEPS.length - 1;

        const dotColor = isDone
          ? (step.key === 'done' ? C.successFg : C.brand)
          : isExpired && i === activeIdx + 1 ? C.dangerFg
          : C.hair2;

        const labelColor = isDone ? (step.key === 'done' ? C.successFg : C.brand2)
          : isActive ? C.ink2 : C.ink3;

        return (
          <React.Fragment key={step.key}>
            <div
              data-testid={`order-pipeline-${order.id}-${step.key}`}
              data-complete={isDone ? 'true' : 'false'}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5 }}
            >
              <div style={{
                width: 8, height: 8, borderRadius: 4,
                background: dotColor,
                boxShadow: isDone ? `0 0 0 2px ${dotColor}33` : 'none',
                transition: 'background .2s',
              }}/>
              <span style={{
                fontFamily: C.mono, fontSize: 10, letterSpacing: 0.5,
                color: labelColor, fontWeight: isDone ? 600 : 400,
                whiteSpace: 'nowrap',
              }}>
                {step.label}
              </span>
            </div>
            {!isLast && (
              <div style={{
                flex: 1, height: 1, margin: '0 4px', marginBottom: 14,
                background: done[PIPELINE_STEPS[i + 1].key] ? C.brand : C.hair,
                transition: 'background .2s',
              }}/>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

const OrderInfo = ({ order, onOrderDelete, onClickShare }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const photoLimit = order.photoCount || 0;
  const photoSelected = order.selectedPhotoCount || 0;
  const photoTotal = order.Album?.pictureCount || 0;
  const progressPct = photoLimit > 0 ? Math.min(100, (photoSelected / photoLimit) * 100) : 0;
  const spaceUsed = order.Album?.spaceUsed > 0 ? getSpaceUsedInUnit(order.Album.spaceUsed) : (order.Album?.pictureCount > 0 ? t('counting...') : '0 B');
  const packName = order.package?.name || '—';
  const expiredTime = order.Album?.expiredTime;
  const hasActiveShare = Boolean(String(order.Album?.password || '').trim());
  const remainingShareDays = getRemainingShareDays(expiredTime);
  const isExpired = order.expiryStatus
    ? order.expiryStatus === 'expired'
    : hasActiveShare && remainingShareDays === 0;
  const isExpiringSoon = order.expiryStatus
    ? order.expiryStatus === 'expiring'
    : hasActiveShare && remainingShareDays > 0 && remainingShareDays <= 7;
  const selectionMeta = selectionStatusMeta(order);
  const paymentMeta = paymentStatusMeta(order);
  const paymentProgress = order.paymentSummary?.payableCount > 0
    ? ` ${order.paymentSummary.paidCount}/${order.paymentSummary.payableCount}`
    : '';
  const shortId = String(order.id || '').length > 8 ? String(order.id).slice(0, 8) : String(order.id || '');
  const workflowStep = orderWorkflowStep(order);

  return (
    <article data-testid={`order-card-${order.id}`} style={{
      background: '#fff',
      border: `1px solid ${C.hair}`,
      borderRadius: 14,
      padding: '24px 26px',
      marginBottom: 18,
      transition: 'border-color .15s',
    }}
    onMouseEnter={e => e.currentTarget.style.borderColor = C.hair2}
    onMouseLeave={e => e.currentTarget.style.borderColor = C.hair}>

      {/* HEAD */}
      <header style={{
        display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between',
        gap: 24, marginBottom: 18,
      }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{
              fontFamily: C.mono, fontSize: 10, letterSpacing: 1.5,
              color: C.ink3, textTransform: 'uppercase',
            }}>
              {shortId?.toUpperCase()} · {packName}
            </span>
            <StatusPill testId={`order-selection-status-${order.id}`} status={selectionMeta.status} tone={selectionMeta.tone}>选片：{selectionMeta.label}</StatusPill>
            <StatusPill testId={`order-payment-status-${order.id}`} status={paymentMeta.status} tone={paymentMeta.tone}>支付：{paymentMeta.label}{paymentProgress}</StatusPill>
            {order.openWatermark && <StatusPill tone="brand">{t('Watermarked')}</StatusPill>}
            {isExpiringSoon && (
              <StatusPill testId={`order-expiring-${order.id}`} tone="danger">即将到期</StatusPill>
            )}
          </div>
          <h2 style={{
            margin: 0,
            fontFamily: C.serif, fontWeight: 400,
            fontSize: 26, lineHeight: 1.1, color: C.ink,
            letterSpacing: -0.3,
          }}>
            {order.customerName || t('Unknown')}
          </h2>
          <div style={{
            marginTop: 4,
            fontFamily: C.mono, fontSize: 11, color: C.ink3,
            letterSpacing: 0.4,
          }}>
            {order.customerPhone && `${order.customerPhone} · `}
            {order.Album?.title && `${order.Album.title} · `}
            {order.createdAt && `创建于 ${dayjs(order.createdAt).format('YYYY·MM·DD')}`}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
          <button
            data-testid={`order-open-current-${order.id}`}
            data-step={workflowStep.id}
            onClick={() => navigate(`/user/orders/${order.id}/${workflowStep.id}`)}
            style={{
              height: 34, padding: '0 12px', borderRadius: 7,
              border: 'none', background: C.ink, color: '#fff',
              cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 7,
              fontFamily: C.sans, fontSize: 12, fontWeight: 500,
            }}
            title={`进入${workflowStep.label}`}
          >
            继续 · {workflowStep.label}<Icon name="arrow" size={13}/>
          </button>
          <button data-testid={`order-share-${order.id}`} onClick={() => onClickShare?.(order)} style={iconBtnStyle} title="分享">
            <Icon name="share" size={15}/>
          </button>
          <button onClick={() => navigate(`/user/orders/${order.id}/accessStats`)} style={iconBtnStyle} title="访问日志">
            <Icon name="stats" size={15}/>
          </button>
          <button data-testid={`order-delete-${order.id}`} onClick={() => onOrderDelete?.(order.id)} style={{ ...iconBtnStyle, color: C.dangerFg }} title="删除">
            <Icon name="delete" size={15}/>
          </button>
        </div>
      </header>

      {/* STATS ROW */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(5, 1fr)',
        gap: 18,
        padding: '14px 0 18px',
        borderTop: `1px solid ${C.hair}`,
        borderBottom: `1px solid ${C.hair}`,
        marginBottom: 16,
      }}>
        <StatCell
          label={t('Packages')}
          value={packName}
          hint={order.pricePerPic ? `¥${formatMoney(order.pricePerPic)}/${t('pics')}` : undefined}
        />
        <StatCell
          label={t('Selected') + ' / ' + t('selectable images')}
          value={<>
            <span style={{ color: C.brand2 }}>{photoSelected}</span>
            <span style={{ color: C.ink3, fontSize: 16 }}> / {photoLimit}</span>
          </>}
          accent
        />
        <StatCell
          label={t('Uploaded pictures')}
          value={photoTotal}
          hint={t('Photos')}
        />
        <StatCell
          label={t('Space Used')}
          value={spaceUsed}
        />
        <StatCell
          testId={`order-expiry-${order.id}`}
          label={t('Expire')}
          value={expiredTime ? (isExpired ? '已过期' : `${remainingShareDays} 天`) : '—'}
          hint={expiredTime ? dayjs(expiredTime).format('YYYY·MM·DD') : t('not shared')}
          warn={isExpired}
        />
      </div>

      {/* STATUS PIPELINE */}
      <StatusPipeline order={order} />

      {/* PHOTO STRIP */}
      <PhotoStrip order={order} count={7}/>

      {/* Remark */}
      {order.remark && (
        <div style={{
          marginTop: 16, paddingTop: 14,
          borderTop: `1px dashed ${C.hair}`,
          fontSize: 13, color: C.ink2,
        }}>
          <span style={{ color: C.ink3 }}>{t('Remark')}: </span>
          {order.remark}
        </div>
      )}
    </article>
  );
};

const iconBtnStyle = {
  width: 34, height: 34, borderRadius: 7,
  border: `1px solid ${C.hair2}`,
  background: '#fff', color: C.ink2,
  cursor: 'pointer',
  display: 'grid', placeItems: 'center',
};

export default OrderInfo;
